import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Info, Check, Stethoscope, Microscope, Syringe, HeartPulse, Users, UserRound, ClipboardList, Snowflake, Brain, Activity, ShieldCheck } from "lucide-react";
import { servicesFallback, fullServicesList, clinicInfo } from "../mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICON_MAP = { Stethoscope, Microscope, Syringe, HeartPulse, Users, UserRound, ClipboardList, Snowflake, Brain, Activity, ShieldCheck };

export default function Services() {
  const [services, setServices] = useState(servicesFallback);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/services`)
      .then((r) => {
        if (!cancelled && Array.isArray(r.data) && r.data.length) setServices(r.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section id="services" className="relative px-4 md:px-8 py-20 md:py-28 molecule-bg">
      <div className="mx-auto max-w-[1400px]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-end mb-14">
          <div>
            <span className="eyebrow">What we offer</span>
            <h2 className="section-title">Comprehensive care<br />for every patient</h2>
          </div>
          <p className="text-[#4a4a63] leading-relaxed lg:max-w-lg lg:ml-auto">
            From everyday consultations to complex skin procedures, our clinic is equipped to care for you and your family at every stage of life. Tap any service to learn more.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s) => {
            const Icon = ICON_MAP[s.icon] || Stethoscope;
            return (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className="group relative bg-white rounded-3xl p-6 border border-[#eee6f7] shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#3dc9b3]/10 grid place-items-center mb-5 group-hover:bg-[#3dc9b3]/20 transition-colors">
                  <Icon className="w-6 h-6 text-[#3dc9b3]" />
                </div>
                <h3 className="font-bold text-[#1a2464] text-[16px] leading-snug mb-2">{s.name}</h3>
                <p className="text-[#4a4a63] text-sm leading-relaxed mb-5">{s.short}</p>
                <div className="flex items-center gap-1.5 text-[#1a2464] font-semibold text-sm group-hover:text-[#3dc9b3] transition-colors">
                  Learn more <ArrowUpRight className="w-4 h-4" />
                </div>
              </Link>
            );
          })}
        </div>

        {/* CTA row */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4">
          <a
            href={clinicInfo.hotdocUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 bg-[#1a2464] text-white rounded-full shadow-sm px-6 py-4 hover:bg-[#141c52]"
          >
            <span className="shrink-0 w-9 h-9 rounded-full bg-[#3dc9b3] grid place-items-center">
              <ArrowRight className="w-4 h-4 text-white" />
            </span>
            <span className="font-semibold flex-1">Book online with HotDoc</span>
            <ArrowUpRight className="w-4 h-4 text-white/70" />
          </a>
          <div className="flex items-center gap-4 bg-white rounded-full shadow-sm px-6 py-4 border border-[#3dc9b3]/30">
            <span className="shrink-0 w-9 h-9 rounded-full bg-[#3dc9b3] grid place-items-center">
              <Info className="w-4 h-4 text-white" />
            </span>
            <span className="text-[#1a1a2e] font-medium flex-1">
              <span className="font-bold text-[#1a2464]">100% Bulk Billed</span> with a valid Medicare card
            </span>
            <a href="/fees" className="text-[#3dc9b3] font-semibold text-sm hover:underline whitespace-nowrap">Learn more</a>
          </div>
        </div>

        {/* Complete list of services */}
        <div id="full-services" className="mt-16 rounded-[32px] bg-white border border-[#eee6f7] shadow-sm p-8 md:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-end mb-8">
            <div>
              <span className="eyebrow">Full list</span>
              <h3 className="text-2xl md:text-3xl font-bold text-[#1a2464] tracking-tight leading-tight">
                Complete list of services we provide
              </h3>
              <p className="text-[#4a4a63] leading-relaxed mt-3 max-w-2xl">
                Our practice provides the community with a comprehensive range of services. Everything below is available at Beaudesert General Practice & Skin Cancer Clinic.
              </p>
            </div>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
            {fullServicesList.map((s) => (
              <li key={s} className="flex items-start gap-3">
                <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-[#3dc9b3]/15 grid place-items-center">
                  <Check className="w-3 h-3 text-[#3dc9b3]" strokeWidth={3} />
                </span>
                <span className="text-[#1a1a2e] text-[15px]">{s}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
